// Enhanced Layout component with better scroll detection
import React, { useContext, useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ThemeContext } from '../../contexts_main/ThemeContext';
import Header from './Header';
import Footer from './Footer';
import { ChevronRightIcon } from '../common/ShellIcons';
import '../../styles_main/components.css';

const Sidebar = React.lazy(() => import('./Sidebar'));

const MOBILE_BREAKPOINT = 768;
const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT;

function Layout() {
  const { darkMode } = useContext(ThemeContext);
  const location = useLocation();
  // Match the first render to the viewport so mobile content does not jump
  // horizontally while the effect below waits to collapse the sidebar.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isMobileViewport);
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // MODIFICATION: Create a ref for the main content area that handles scrolling
  const mainContentRef = useRef(null);
  
  const isNewsAtlasPage = location.pathname === '/' ||
                         location.pathname.startsWith('/countries/') ||
                         location.pathname.startsWith('/organizations/') ||
                         location.pathname.startsWith('/people/') ||
                         location.pathname.startsWith('/regions/');
  const isTradePage = location.pathname === '/trade' ||
                      location.pathname.startsWith('/trade/');
  
  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const mobile = isMobileViewport();
      setIsMobile(mobile);
      
      // Auto-collapse sidebar on mobile
      if (mobile) {
        setSidebarCollapsed(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const toggleSidebar = () => {
    setSidebarCollapsed((collapsed) => !collapsed);
  };

  // Make the collapse function available globally for Home component
  useEffect(() => {
    const collapseSidebarOnMobile = () => {
      if (isMobile) {
        setSidebarCollapsed(true);
      }
    };

    window.collapseSidebarOnMobile = collapseSidebarOnMobile;
    return () => {
      delete window.collapseSidebarOnMobile;
    };
  }, [isMobile]);

  useEffect(() => {
    const contentElement = mainContentRef.current;
    if (!contentElement) return undefined;

    // Function to scroll to the top of the main content area
    const scrollToTop = () => {
      contentElement.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };

    // Make the scrollToTop function available globally
    window.scrollToTopMain = scrollToTop;

    const nestedScrollState = (scrollTarget) => {
      if (contentElement.scrollTop > 10) return true;
      if (!isTradePage || scrollTarget === contentElement) return false;
      return Number(scrollTarget?.scrollTop || 0) > 10;
    };

    let animationFrame;
    let lastScrolled = nestedScrollState(contentElement);
    setIsScrolled(lastScrolled);

    const handleScroll = (event) => {
      if (animationFrame !== undefined) return;
      const scrollTarget = event.target;
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = undefined;
        const nextScrolled = nestedScrollState(scrollTarget);
        if (nextScrolled === lastScrolled) return;
        lastScrolled = nextScrolled;
        setIsScrolled(nextScrolled);
      });
    };

    contentElement.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: isTradePage
    });

    return () => {
      contentElement.removeEventListener('scroll', handleScroll, isTradePage);
      if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
      delete window.scrollToTopMain;
    };
  }, [isTradePage]);
  
  return (
    <div className={`layout ${darkMode ? 'dark-mode' : 'light-mode'} pattern-background-static`}>
      {/* MODIFICATION: Pass the isScrolled state to the Header */}
      <Header isScrolled={isScrolled} />
      
      <div className={`layout-container ${!isNewsAtlasPage ? 'no-sidebar' : ''}`}>
        {isNewsAtlasPage && (
          <>
            <aside className={`sidebar-container ${sidebarCollapsed ? 'collapsed' : ''}`}>
              <React.Suspense fallback={null}>
                <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
              </React.Suspense>
            </aside>
            
            {sidebarCollapsed && (
              <button 
                className="sidebar-expand-button"
                onClick={toggleSidebar}
                title="Expand sidebar"
              >
                <ChevronRightIcon />
              </button>
            )}
          </>
        )}
        
        {/* MODIFICATION: Attach the ref to the main scrollable content area */}
        <main 
          ref={mainContentRef}
          className={`main-content ${!isNewsAtlasPage ? 'full-width' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${isTradePage ? 'trade-route-main' : ''}`}
        >
          <div className={`content-wrapper ${isTradePage ? 'trade-route-content' : ''}`}>
            <Outlet context={{ sidebarCollapsed }} />
          </div>
        </main>
      </div>
      
      <Footer />
    </div>
  );
}

export default Layout;

